import { useState, useEffect, createContext, useContext } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import {
  ClerkProvider,
  SignedIn,
  SignedOut,
  RedirectToSignIn,
  SignIn,
  SignUp,
  useAuth,
} from "@clerk/clerk-react";
import { esES } from "@clerk/localizations";
import { setAuthTokenGetter } from "@/lib/api";

const CLERK_PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;
if (!CLERK_PUBLISHABLE_KEY) {
  // eslint-disable-next-line no-console
  console.error("Falta REACT_APP_CLERK_PUBLISHABLE_KEY en build env");
}

function ClerkTokenBridge() {
  const { getToken, isSignedIn } = useAuth();
  useEffect(() => {
    setAuthTokenGetter(async () => (isSignedIn ? await getToken() : null));
  }, [getToken, isSignedIn]);
  return null;
}

// Legacy redirect component that preserves ejercicioId
function LegacyRedirect({ tab }) {
  const { ejercicioId } = useParams();
  return <Navigate to={`/ejercicios/${ejercicioId}/analisis?tab=${tab}`} replace />;
}

// Pages
import DashboardLayout from "@/components/layout/DashboardLayout";
import EmpresasPage from "@/pages/EmpresasPage";
import HomePage from "@/pages/HomePage";
import EmpresaDetallePage from "@/pages/EmpresaDetallePage";
import ImportarPage from "@/pages/ImportarPage";
import RevisionPage from "@/pages/RevisionPage";
import AsientosPage from "@/pages/AsientosPage";
import EjercicioAnalisisPage from "@/pages/EjercicioAnalisisPage";
import ComparativaPage from "@/pages/ComparativaPage";
import ComparativaAniosPage from "@/pages/ComparativaAniosPage";
import ComparativaFlujosPage from "@/pages/ComparativaFlujosPage";
import ExportacionesPage from "@/pages/ExportacionesPage";
import ProyeccionPage from "@/pages/ProyeccionPage";
import ResumenAnalisisPage from "@/pages/ResumenAnalisisPage";
import DfnPage from "@/pages/DfnPage";
import InvitacionesPage from "@/pages/InvitacionesPage";
import ConectarIAPage from "@/pages/ConectarIAPage";
import DiagnosticoPage from "@/pages/DiagnosticoPage";
import SaludEmpresaPage from "@/pages/SaludEmpresaPage";
import ConectarFinoviewPage from "@/pages/ConectarFinoviewPage";
import ConectarHoldedPage from "@/pages/ConectarHoldedPage";
import ContabilidadHoldedPage from "@/pages/ContabilidadHoldedPage";
import ConstruccionEfePage from "@/pages/ConstruccionEfePage";
// Plan de seguimiento (fuera de auth, gate por password client-side)
import PlanPage from "@/pages/PlanPage";

// Theme Context
const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
};

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("theme") || "light";
    }
    return "light";
  });

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

function ProtectedShell() {
  return (
    <>
      <SignedIn>
        <DashboardLayout />
      </SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  );
}

function App() {
  return (
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      localization={esES}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      signInFallbackRedirectUrl="/empresas"
      signUpFallbackRedirectUrl="/empresas"
    >
      <ClerkTokenBridge />
      <ThemeProvider>
        <BrowserRouter>
          <Routes>
            {/* Plan público con password client-side, fuera del shell autenticado */}
            <Route path="/plan" element={<PlanPage />} />
            <Route
              path="/sign-in/*"
              element={
                <div className="min-h-screen flex flex-col items-center justify-center gap-8 p-6">
                  <img
                    src="/images/PF_logo_transparente.png"
                    alt="PF advisors"
                    className="h-20 w-auto"
                  />
                  <SignIn routing="path" path="/sign-in" />
                </div>
              }
            />
            <Route
              path="/sign-up/*"
              element={
                <div className="min-h-screen flex flex-col items-center justify-center gap-8 p-6">
                  <img
                    src="/images/PF_logo_transparente.png"
                    alt="PF advisors"
                    className="h-20 w-auto"
                  />
                  <SignUp routing="path" path="/sign-up" />
                </div>
              }
            />
            <Route path="/" element={<ProtectedShell />}>
              <Route index element={<Navigate to="/inicio" replace />} />
              {/* Inicio: panel de la empresa seleccionada (rediseño oct-2026) */}
              <Route path="inicio" element={<HomePage />} />
              <Route path="empresas" element={<EmpresasPage />} />
              <Route path="empresas/:empresaId" element={<EmpresaDetallePage />} />
              {/* Salud de la empresa (diagnóstico general) */}
              <Route path="empresas/:empresaId/salud" element={<SaludEmpresaPage />} />
              {/* Conectar / importar desde Finoview */}
              <Route path="empresas/:empresaId/finoview" element={<ConectarFinoviewPage />} />
              {/* Conectar Holded (diario -> Sumas y Saldos) */}
              <Route path="empresas/:empresaId/holded" element={<ConectarHoldedPage />} />
              <Route path="ejercicios/:ejercicioId/importar" element={<ImportarPage />} />
              <Route path="ejercicios/:ejercicioId/revision" element={<RevisionPage />} />
              <Route path="ejercicios/:ejercicioId/asientos" element={<AsientosPage />} />
              <Route path="ejercicios/:ejercicioId/contabilidad" element={<ContabilidadHoldedPage />} />
              <Route path="ejercicios/:ejercicioId/efe" element={<ConstruccionEfePage />} />
              {/* New unified analysis page with tabs */}
              <Route path="ejercicios/:ejercicioId/analisis" element={<EjercicioAnalisisPage />} />
              {/* Legacy routes redirect to new analysis page with tab param */}
              <Route path="ejercicios/:ejercicioId/dashboard" element={<LegacyRedirect tab="dashboard" />} />
              <Route path="ejercicios/:ejercicioId/balance" element={<LegacyRedirect tab="balance" />} />
              <Route path="ejercicios/:ejercicioId/pyg" element={<LegacyRedirect tab="pyg" />} />
              <Route path="ejercicios/:ejercicioId/ebitda" element={<LegacyRedirect tab="ebitda" />} />
              {/* Comparativas */}
              <Route path="empresas/:empresaId/resumen" element={<ResumenAnalisisPage />} />
              <Route path="empresas/:empresaId/dfn" element={<DfnPage />} />
              <Route path="empresas/:empresaId/comparativa" element={<ComparativaPage />} />
              <Route path="empresas/:empresaId/comparativa-anios" element={<ComparativaAniosPage />} />
              <Route path="empresas/:empresaId/comparativa-flujos" element={<ComparativaFlujosPage />} />
              {/* Exportaciones */}
              <Route path="empresas/:empresaId/exportaciones" element={<ExportacionesPage />} />
              {/* Proyección */}
              <Route path="empresas/:empresaId/proyeccion" element={<ProyeccionPage />} />
              {/* Invitaciones */}
              <Route path="invitaciones" element={<InvitacionesPage />} />

              {/* Conectar con ChatGPT / Claude (servidor MCP, ver backend/mcp_service/README.md) */}
              <Route path="conectar-ia" element={<ConectarIAPage />} />
              {/* Diagnóstico del motor de cálculo */}
              <Route path="diagnostico" element={<DiagnosticoPage />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </ThemeProvider>
    </ClerkProvider>
  );
}

export default App;
